import React, { createContext, useContext, useEffect, useState } from 'react';
import { db, collection, onSnapshot, query, where, setDoc, deleteDoc, doc, handleFirestoreError, OperationType, serverTimestamp } from './firebase';
import { useAuth } from './AuthContext';
import { getSafeSolicitationDocId } from './utils/firestoreSanitizer';

export interface SavedSolicitation {
  id: string; // e.g. `${userId}_${solicitationId}`
  userId: string;
  solicitationId: string;
  title: string;
  state: string;
  agency: string;
  dueDate: string;
  reminderDaysBefore: number;
  createdAt?: any;
}

export interface ToastMessage {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'danger';
  solicitationId?: string;
  daysRemaining?: number;
}

interface SavedContextType {
  savedList: SavedSolicitation[];
  loading: boolean;
  isSaved: (solicitationId: string) => boolean;
  toggleSave: (sol: any) => Promise<void>;
  updateReminderPreference: (solicitationId: string, daysBefore: number) => Promise<void>;
  toasts: ToastMessage[];
  addToast: (toast: Omit<ToastMessage, 'id'>) => void;
  removeToast: (id: string) => void;
  checkDeadlines: () => void;
}

const SavedContext = createContext<SavedContextType>({
  savedList: [],
  loading: true,
  isSaved: () => false,
  toggleSave: async () => {},
  updateReminderPreference: async () => {},
  toasts: [],
  addToast: () => {},
  removeToast: () => {},
  checkDeadlines: () => {}
});

export const SavedSolicitationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [savedList, setSavedList] = useState<SavedSolicitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [hasScannedDeadlines, setHasScannedDeadlines] = useState(false);

  // Real-time synchronization of saved solicitations
  useEffect(() => {
    if (!user) {
      setSavedList([]);
      setLoading(false);
      setHasScannedDeadlines(false);
      setToasts([]);
      return;
    }

    setLoading(true);
    const q = query(collection(db, 'saved_solicitations'), where('userId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      })) as SavedSolicitation[];
      
      setSavedList(data);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'saved_solicitations');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Toast utilities
  const addToast = (toastInput: Omit<ToastMessage, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, { id, ...toastInput }]);
    
    // Auto-remove toast after 10 seconds for user convenience
    setTimeout(() => {
      removeToast(id);
    }, 10000);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Check upcoming deadlines and present custom UI toasts
  const checkDeadlines = () => {
    if (savedList.length === 0) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    savedList.forEach(item => {
      if (!item.dueDate) return;
      const due = new Date(item.dueDate);
      due.setHours(0, 0, 0, 0);
      
      const diffTime = due.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const threshold = item.reminderDaysBefore || 3;

      // Only notify if within user threshold, and not in the past
      if (diffDays >= 0 && diffDays <= threshold) {
        let typeVal: 'warning' | 'danger' | 'info' = 'info';
        let levelLabel = 'Upcoming';

        if (diffDays <= 1) {
          typeVal = 'danger';
          levelLabel = '⚠️ Urgent deadline tomorrow';
        } else if (diffDays <= 3) {
          typeVal = 'warning';
          levelLabel = '⏳ Deadline approaching';
        }

        addToast({
          title: item.title,
          message: `${levelLabel}: due in ${diffDays} day${diffDays !== 1 ? 's' : ''} (${due.toLocaleDateString()}).`,
          type: typeVal,
          solicitationId: item.solicitationId,
          daysRemaining: diffDays
        });
      }
    });
  };

  // Once loading completes, trigger deadline-check once per user login session
  useEffect(() => {
    if (!loading && savedList.length > 0 && !hasScannedDeadlines) {
      checkDeadlines();
      setHasScannedDeadlines(true);
    }
  }, [loading, savedList.length, hasScannedDeadlines]);

  const isSaved = (solicitationId: string) => {
    return savedList.some(item => item.solicitationId === solicitationId);
  };

  const toggleSave = async (sol: any) => {
    if (!user) return;
    const docId = `${user.uid}_${getSafeSolicitationDocId(sol.solicitationId)}`;
    
    // Check if nested in existing list
    const existingIndex = savedList.findIndex(item => item.solicitationId === sol.solicitationId);
    
    if (existingIndex > -1) {
      // Delete bookmark
      try {
        await deleteDoc(doc(db, 'saved_solicitations', docId));
        // Also show alert/toast of successful untracking
        addToast({
          title: sol.title,
          message: 'Removed from watch list and reminder notifications.',
          type: 'info'
        });
      } catch (error) {
        handleFirestoreError(error, OperationType.DELETE, `saved_solicitations/${docId}`);
      }
    } else {
      // Add bookmark
      try {
        const savedData: SavedSolicitation = {
          id: docId,
          userId: user.uid,
          solicitationId: sol.solicitationId,
          title: sol.title,
          state: sol.state || 'N/A',
          agency: sol.agency || 'N/A',
          dueDate: sol.dueDate || '',
          reminderDaysBefore: 3, // default threshold in days
          createdAt: serverTimestamp()
        };
        await setDoc(doc(db, 'saved_solicitations', docId), savedData);
        addToast({
          title: sol.title,
          message: 'Saved to watch list! You will receive countdown alerts as the bid deadline approaches.',
          type: 'success',
          solicitationId: sol.solicitationId
        });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, `saved_solicitations/${docId}`);
      }
    }
  };

  const updateReminderPreference = async (solicitationId: string, daysBefore: number) => {
    if (!user) return;
    const docId = `${user.uid}_${getSafeSolicitationDocId(solicitationId)}`;
    try {
      await setDoc(doc(db, 'saved_solicitations', docId), {
        reminderDaysBefore: daysBefore
      }, { merge: true });

      // Notify user of custom setting update
      const updatedS = savedList.find(item => item.solicitationId === solicitationId);
      if (updatedS) {
        addToast({
          title: updatedS.title,
          message: `Notification threshold updated to ${daysBefore} days before deadline.`,
          type: 'success'
        });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `saved_solicitations/${docId}`);
    }
  };

  return (
    <SavedContext.Provider value={{
      savedList,
      loading,
      isSaved,
      toggleSave,
      updateReminderPreference,
      toasts,
      addToast,
      removeToast,
      checkDeadlines
    }}>
      {children}
    </SavedContext.Provider>
  );
};

export const useSavedSolicitations = () => useContext(SavedContext);
